import type { MetaDescriptor } from "react-router"
import type { Locale } from "../content/types"
import type { RegistryEntry } from "../content/registry"
import { getAlternates } from "../content/registry"
import { SITE_URL, HREFLANG_TAGS, X_DEFAULT_LOCALE, DEFAULT_OG_IMAGE, SITE_NAME } from "../content/site"

function absoluteUrl(path: string): string {
  return `${SITE_URL}${path}`
}

export interface PageMetaInput {
  entry: RegistryEntry
  locale: Locale
  image?: string
}

/**
 * Builds the full metadata set for one page:
 *   - title & meta description (route-specific, never global fallbacks)
 *   - self-referencing absolute canonical URL
 *   - Open Graph + Twitter card data
 *   - reciprocal hreflang alternates incl. x-default
 *   - noindex directive when a page is not indexable
 *
 * Canonical URLs are derived exclusively from the registry — never from
 * window.location.
 */
export function buildPageMeta({ entry, locale, image }: PageMetaInput): MetaDescriptor[] {
  const title = entry.title[locale]
  const description = entry.description[locale]
  const canonicalPath = entry.path[locale]
  if (!title || !description || !canonicalPath) {
    throw new Error(`Registry entry "${entry.id}" is missing metadata for locale "${locale}".`)
  }

  const canonicalUrl = absoluteUrl(canonicalPath)
  const ogImageUrl = absoluteUrl(image ?? DEFAULT_OG_IMAGE)

  const htmlLangByLocale: Record<Locale, string> = {
    nl: "nl_NL",
    en: "en_GB",
  }

  const meta: MetaDescriptor[] = [
    { title },
    { name: "description", content: description },

    // Canonical: every indexable page references itself.
    // The English equivalent canonicalizes to itself, not to the Dutch page.
    { tagName: "link", rel: "canonical", href: canonicalUrl },

    // Reciprocal hreflang alternates.
    // NOTE: attribute keys are rendered verbatim by react-dom/server, so we
    // emit the lowercase HTML attribute name directly.
    ...(Object.entries(getAlternates(entry)) as [Locale, string][]).flatMap<MetaDescriptor>(
      ([altLocale, altPath]) => [
        { tagName: "link", rel: "alternate", ["hreflang" as string]: HREFLANG_TAGS[altLocale], href: absoluteUrl(altPath) },
      ],
    ),
    ...(entry.path[X_DEFAULT_LOCALE]
      ? [{ tagName: "link", rel: "alternate", ["hreflang" as string]: "x-default", href: absoluteUrl(entry.path[X_DEFAULT_LOCALE]!) }]
      : []),

    // Open Graph
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalUrl },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:image", content: ogImageUrl },
    { property: "og:image:alt", content: SITE_NAME },
    { property: "og:locale", content: htmlLangByLocale[locale] },
    ...(Object.entries(getAlternates(entry)) as [Locale, string][])
      .filter(([l]) => l !== locale)
      .map<MetaDescriptor>(([l]) => ({ property: "og:locale:alternate", content: htmlLangByLocale[l] })),

    // Twitter card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImageUrl },
    { name: "twitter:image:alt", content: SITE_NAME },
  ]

  if (!entry.indexable) {
    meta.push({ name: "robots", content: "noindex, follow" })
  }

  return meta
}
